import $ from 'jquery';
import _ from 'underscore';
import Environment from '../environment';
import Eventable from '../eventable';
import { getLogger } from '../logging/logger';
import gsap from '../util/hwpTVGSAP';
import { EVENTS, FADE_DURATION, SOUNDS, VOLUME_BG_DEFAULT } from './audioManager.types';

/**
 * @typedef {import('./audioManager.types').AudioManagerOptions} AudioManagerOptions
 * @typedef {import('./audioManager.types').HwpTvAudioData} HwpTvAudioData
 * @typedef {import('./audioManager.types').HwpTvAudio} HwpTvAudio
 */

const logger = getLogger('AudioManager');

class AudioManager extends Eventable {
	/**
	 * Constructs an AudioManager instance with the provided options.
	 *
	 * @param {AudioManagerOptions} options - The options to initialize the AudioManager with.
	 */
	constructor(options) {
		logger.log('[initialize]', { options });
		super();

		/** @type {string} */
		this.contentUrl = '/hwpHub/json/audioData.json';
		this.tracks = [];
		/** @type {HwpTvAudioData[]} */
		this.data = [];
		/** @type {HwpTvAudio} */
		this.currentAudio = new Audio();
		/** @type {HTMLAudioElement[]} */
		this.activeSounds = [];
		this.muted = false;

		if (this.supportsMultipleTracks()) {
			this.backgroundAudio = new Audio();
			this.backgroundAudio.loop = true;
			this.backgroundAudio.volume = VOLUME_BG_DEFAULT;
		}

		this.initialize(options);
	}

	/**
	 * Initializes the AudioManager with the provided options.
	 *
	 * @param {AudioManagerOptions} options - The options to initialize the AudioManager with.
	 * @returns {Promise<void>} A promise that resolves when the AudioManager is initialized.
	 */
	initialize(options) {
		logger.log('[Initialize]', { options });

		Object.assign(this, options);

		return fetch(this.contentUrl)
			.then((response) => response.json())
			.then(({ data = [] }) => {
				logger.debug('[data loaded]', { data });
				this.data = data;
				this.trigger(EVENTS.initialized);
			});
	}

	/**
	 * Plays a sound with the given soundId.
	 *
	 * @param {SOUNDS} soundId - The ID of the sound to play.
	 * @returns {Promise<void>} A promise that resolves when the sound has finished playing.
	 */
	playSound(soundId) {
		logger.log('[playSound]', { soundId });

		if (this.muted) {
			return Promise.resolve();
		}

		if (!soundId) {
			logger.warn('Missing soundId');
			return Promise.reject(new Error('Missing soundId'));
		}

		if (this.isVideoPlaying() && !this.supportsMultipleTracks()) {
			logger.warn('[Sounds disabled while video is playing]');
			return Promise.resolve();
		}

		const sndFile = this.data.find(({ id }) => id === soundId);
		if (!sndFile) {
			logger.warn(`Unknown Audio "${soundId}"`);
			return Promise.resolve();
		}

		// Get source url for audio or exit
		const { priority = 1 } = sndFile;
		let { /** @type {undefined|string|string[]} */ src } = sndFile;
		if (Array.isArray(src)) {
			src = _.sample(src);
		}
		if (!src) {
			logger.log('Invalid sound source url', { soundId });
			return Promise.resolve();
		}
		const currentAudio = this.getAudioObject();

		if (
			currentAudio.src &&
			currentAudio.priority !== undefined &&
			currentAudio.priority > priority
		) {
			logger.warn(`${soundId} has a lower priority than what's playing:`, {
				[`${soundId}Priority`]: priority,
				currentAudioSrc: currentAudio.src,
				currentAudioPriority: currentAudio.priority,
			});
			return Promise.resolve();
		}

		// Clean up any existing audio
		if (!currentAudio.paused) {
			currentAudio.pause();
		}

		if (this.activeSounds && this.activeSounds.indexOf(currentAudio) < 0) {
			this.activeSounds.push(currentAudio);
		}

		currentAudio.src = src;
		currentAudio.priority = priority;
		currentAudio.load();
		currentAudio.play();

		return new Promise((resolve) => {
			$(currentAudio).one('ended', () => {
				if (this.activeSounds && this.activeSounds.indexOf(currentAudio) >= 0) {
					const soundIndex = this.activeSounds.indexOf(currentAudio);
					this.activeSounds.splice(soundIndex, 1);
					// Reset current audio once it's ended
					this.currentAudio.priority = undefined;
					this.currentAudio.src = '';
					this.currentAudio.load();
				}
				resolve();
			});
		});
	}

	/**
	 * Returns an audio instance to use. In environments multiple audio is supported a new Audio
	 * object will be returned. In environments where only 1 sound is sound is supported a single Audio
	 * object will be returned.
	 * @returns {HwpTvAudio}
	 */
	getAudioObject() {
		logger.log('[getAudioObject]');
		if (this.supportsMultipleTracks()) {
			return new Audio();
		}

		if (!this.currentAudio) {
			this.currentAudio = new Audio();
			this.currentAudio.priority = 1;
		}

		return this.currentAudio;
	}

	/**
	 * Plays the background music for the application.
	 *
	 * @param {SOUNDS} soundId - The ID of the sound file to play as the background music.
	 * @returns {Promise<void>} - A Promise that resolves when the background music has started playing.
	 */
	playBackgroundMusic(soundId) {
		logger.log('[PlayBackgroundMusic]', { soundId });

		if (!this.supportsMultipleTracks()) {
			return Promise.reject(new Error('Background music is not supported'));
		}

		if (!soundId) {
			logger.warn('Missing soundId');
			return Promise.reject(new Error('Missing soundId'));
		}

		if (this.isVideoPlaying()) {
			logger.warn('return because video is playing');
			return Promise.reject(new Error('Video in progress'));
		}

		const { id, src } = this.data.find(({ id }) => id === soundId) || {};
		logger.info({ currentSrc: this.backgroundAudio?.src, src });

		if (!id || !src) {
			return Promise.reject(new Error(`Missing Sound data for ${soundId}`));
		}

		/**
		 * ! FIXME We don't want to reload if the src is the same, but accessing `backgroundAudio.src`
		 * ! returns the full path and our `src` is just a relative path. Not sure how to account for bugs
		 * ! such as if src is just a single letter (it create lots of false matches)
		 */
		if (src && !this.backgroundAudio.src.includes(src)) {
			this.backgroundAudio.src = src;
			this.backgroundAudio.load();
			return this.resumeBackgroundMusic();
		}

		return Promise.resolve();
	}

	/**
	 * Resumes the playback of the background music.
	 *
	 * This method checks if the `backgroundAudio` property is defined and if it is currently paused.
	 * If so, it calls the `play()` method on the `backgroundAudio` object to resume the playback
	 * of the background music.
	 */
	resumeBackgroundMusic() {
		if (this.backgroundAudio?.paused) {
			this.backgroundAudio.play();
			return gsap.to(this.backgroundAudio, {
				volume: VOLUME_BG_DEFAULT,
				duration: FADE_DURATION,
			});
		}

		return Promise.resolve();
	}

	/**
	 * Stops all currently playing sounds.
	 *
	 * This method pauses the current audio playback and clears the source of the `currentAudio` object.
	 */
	stopAllSounds() {
		logger.log('[stopAllSounds]');

		if (this.currentAudio) {
			this.currentAudio.pause();
			this.currentAudio.src = '';
		}
	}

	/**
	 * Pauses the playback of the background music.
	 *
	 * This method checks if the `backgroundAudio` property is defined and if it is currently playing.
	 * If so, it calls the `pause()` method on the `backgroundAudio` object to pause the playback
	 * of the background music.
	 */
	pauseBackgroundMusic() {
		if (this.backgroundAudio) {
			return gsap
				.to(this.backgroundAudio, { volume: 0, duration: FADE_DURATION })
				.then(() => {
					this.backgroundAudio.pause();
				});
		}

		return Promise.resolve();
	}

	/**
	 * Stops all currently playing audio, including background music.
	 *
	 * This method calls `stopAllSounds()` to pause and clear the source of the
	 * currently playing audio, and then calls `pauseBackgroundMusic()` to pause
	 * the playback of the background music.
	 */
	stopAllAudio() {
		this.stopAllSounds();
		this.pauseBackgroundMusic();
	}

	/**
	 * Checks if any video elements on the page are currently playing.
	 *
	 * This method iterates through all video elements on the page and checks if the
	 * `currentTime` property is greater than 0 and the `paused` property is false,
	 * indicating that the video is currently playing. It returns `true` if any video
	 * is playing, and `false` otherwise.
	 *
	 * @returns {boolean} `true` if any video is currently playing, `false` otherwise.
	 */
	isVideoPlaying() {
		logger.log('[isVideoPlaying]');

		return $('video')
			.toArray()
			.some((/** @type {HTMLVideoElement} */ vid) => vid.currentTime > 0 && !vid.paused);
	}

	/**
	 * Checks if the current environment supports playing multiple audio tracks simultaneously.
	 *
	 * This method returns `true` if the current environment is not a set-top box, as set-top boxes
	 * often have limited support for playing multiple audio tracks at the same time. This is used
	 * to determine if certain audio features can be enabled or not.
	 *
	 * @returns {boolean} `true` if the current environment supports playing multiple audio tracks,
	 * `false` otherwise.
	 */
	supportsMultipleTracks() {
		const supports =
			!Environment.isComcastSetTopBox() &&
			!Environment.isVerizon1100() &&
			!Environment.isVerizon4100();
		logger.log('[supportsMultipleTracks]', supports);
		return supports;
	}
}

export default new AudioManager();
